// @react
import React, {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
// @types
import { AutoRenewFormValues } from 'modules/common/types'
import { AutoRenewalSubmitData, MembershipContext } from './index'
// @libraries
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { DevTool } from '@hookform/devtools'
// @components
import Grid from '@bit/atd.web.grid'
import TextLink from '@bit/atd.web.text-link'
import Spacer from '@bit/atd.web.spacer'
import Typography from '@material-ui/core/Typography'
import Modal from '@bit/atd.web.modal'
import InputText from '@bit/atd.web.forms.input-text'
import Button from '@bit/atd.web.button'
import InputCheckbox from '@bit/atd.web.forms.input-checkbox'
import InputSectionLabel from '@bit/atd.web.forms.input-section-label'
// @module components
import EditingAction from 'modules/profile/components/EditingAction'
import CreditCardFields from 'modules/profile/components/Membership/Form/CreditCard'
import { validationSchema } from 'modules/common/forms/validations/autoRenew'
import { ExternalRouteNames } from 'constants/routeNames'

type PropsType = {
  renewModal: boolean
  membershipContext: any
  setRenewModal: Dispatch<SetStateAction<boolean>>
  renewalHandler: (
    data: AutoRenewalSubmitData,
    targetAutorenewal: boolean,
    context: MembershipContext,
  ) => Promise<boolean | void>
  isMutationSubmitting?: boolean
}

const AutoRenewForm = ({
  membershipContext,
  renewModal,
  setRenewModal,
  renewalHandler,
  isMutationSubmitting,
}: PropsType): ReactElement => {
  /******************************
   *  STATE
   *******************************/

  const [showThankYouModal, setShowThankYouModal] = useState<boolean>(false)

  /******************************
   *  REFS
   *******************************/

  const nameRef = useRef<HTMLInputElement | null>(null)
  const agreeTermsRef = useRef<HTMLInputElement | null>(null)

  /******************************
   *  HOOKS
   *******************************/

  const formMethods = useForm<AutoRenewFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      orderNumber: '',
      orderLineNumber: '',
      name: '',
      cardType: '',
      cardNumber: '',
      cardExpirationDate: '',
      cardSecurityCode: '',
      agreeTerms: false,
    },
  })
  const { control, formState, handleSubmit, reset } = formMethods
  const { errors } = formState

  useEffect(() => {
    reset({
      orderNumber: membershipContext?.orderNumber,
      orderLineNumber: membershipContext?.orderLineNumber,
      name: '',
      cardType: '',
      cardNumber: '',
      cardExpirationDate: '',
      cardSecurityCode: '',
      agreeTerms: false,
    })
  }, [reset, membershipContext])

  /******************************
   *  FUNCTION
   *******************************/

  const onCancelHandler = () => {
    setRenewModal(false)
    formMethods.clearErrors()
    formMethods.reset()
  }

  const onSubmitHandler = async (formValues: AutoRenewFormValues) => {
    const splitExpiryDate = formValues.cardExpirationDate.split('/') // @todo not good enough to set assumed, fix form typing

    const res = await renewalHandler(
      {
        orderNumber: formValues.orderNumber,
        orderLineNumber: formValues.orderLineNumber,
        creditCardType: formValues.cardType,
        creditCardNumber: formValues.cardNumber,
        creditCardSecurityCode: formValues.cardSecurityCode,
        nameOnCard: formValues.name,
        expirationYear: parseInt(`20${splitExpiryDate[1]}`), // i guess we can assume we won't be maintaining this past the 21st century
        expirationMonth: parseInt(splitExpiryDate[0]),
      },
      true,
      membershipContext,
    )

    if (res) {
      setRenewModal(false)
      setShowThankYouModal(true)
    }
  }

  const onThankYouConfirmationHandler = () => {
    setShowThankYouModal(false)
  }

  /******************************
   *  RENDER
   *******************************/

  return (
    <>
      {process.env.DEBUG !== '1' && <DevTool control={control} />}

      {/* Auto-Renew Payment Form Modal */}
      <Modal
        open={renewModal}
        onClose={() => setRenewModal(false)}
        title="Auto-renew payment information"
        actions={
          <EditingAction
            isSubmitting={isMutationSubmitting}
            submitButtonText="Save"
            onCancel={() => onCancelHandler()}
            onSubmit={handleSubmit((data) => onSubmitHandler(data))}
          />
        }
      >
        <form>
          <Grid container spacing={4}>
            {/* Legend */}
            <Grid xs={12} item>
              <InputSectionLabel component={'legend'}>
                Payment Card
              </InputSectionLabel>
            </Grid>

            <Controller
              control={control}
              name="orderNumber"
              render={(props) => <input hidden type="text" {...props} />}
            />

            <Controller
              control={control}
              name="orderLineNumber"
              render={(props) => <input hidden type="text" {...props} />}
            />

            {/* Name on card */}
            <Grid xs={12} sm={6} item>
              <Controller
                control={control}
                onFocus={() => nameRef?.current?.focus()}
                name="name"
                render={({ onChange, onBlur, value, name }) => (
                  <InputText
                    required
                    name={name}
                    label="Name on Card"
                    placeholder="Enter your full name."
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    inputRef={nameRef}
                    error={!!errors?.name}
                    errorText={errors?.name?.message}
                  />
                )}
              />
            </Grid>

            {/* Credit Card Inputs */}
            <CreditCardFields formMethods={formMethods} control={control} />
          </Grid>

          {/* Agree Terms */}
          <Grid container spacing={4}>
            <Grid xs={12} item>
              <Controller
                control={control}
                onFocus={() => agreeTermsRef?.current?.focus()}
                name="agreeTerms"
                render={({ onBlur, value, name }) => (
                  <InputCheckbox
                    required
                    name={name}
                    label={
                      <>
                        I agree to the{' '}
                        <TextLink
                          href={ExternalRouteNames.ATD_TOS}
                          variant="primary"
                        >
                          ATD terms and conditions,
                        </TextLink>{' '}
                        including the automatic renewal provisions of the offer.
                        By checking this box, I also agree that I am at least 18
                        years of age and that I am electronically signing my
                        order and authorizing ATD to charge payments against the
                        payment card provided today.
                      </>
                    }
                    labelPlacement="right"
                    placeholder="Select your age."
                    size="small"
                    checked={value}
                    onBlur={onBlur}
                    onChange={() =>
                      formMethods.setValue('agreeTerms', !value, {
                        shouldValidate: false,
                        shouldDirty: true,
                      })
                    }
                    inputRef={agreeTermsRef}
                    error={!!errors?.agreeTerms}
                    errorText={errors?.agreeTerms?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Spacer fullWidth size={24} />

          {/* Disclaimers */}
          <Grid container>
            <Grid xs={12} item>
              <Typography paragraph component={'p'} variant="caption">
                If you decide you do not wish to have your order automatically
                renew, you can cancel the automatic renewal at anytime within
                thirty (30) days prior to the renewal of the order and payment.
                To do so, toggle the auto-renew off.
              </Typography>
              <Typography component={'p'} variant="caption">
                If you have any questions, please contact Customer Care at{' '}
                <TextLink
                  href={ExternalRouteNames.ATD_PHONE_GLOBAL}
                  variant="primary"
                >
                  800.628.2783
                </TextLink>{' '}
                (US) or{' '}
                <TextLink
                  href={ExternalRouteNames.ATD_PHONE_US}
                  variant="primary"
                >
                  703.683.8100
                </TextLink>{' '}
                (international), Monday through Friday 8 a.m-6 p.m ET or{' '}
                <TextLink
                  href={ExternalRouteNames.ATD_MAIL_CUSTOMER_CARE}
                  variant="primary"
                >
                  customercare@td.org
                </TextLink>
                .
              </Typography>
            </Grid>
          </Grid>
        </form>
      </Modal>

      {/* Thank You Confirmation Modal */}
      <Modal
        footerNoSeparator
        headerNoSeparator
        open={showThankYouModal}
        size="medium"
        title="Thank you for signing up for auto renewal!"
      >
        <Typography paragraph>
          Your payment information has been securely stored and will be used
          when your order is renewed for its next cycle.
        </Typography>
        <Typography paragraph>
          If you wish to cancel auto renewal, you can do so at any time.
        </Typography>
        <Button
          variant="primary"
          size="medium"
          onClick={onThankYouConfirmationHandler}
        >
          Okay
        </Button>
      </Modal>
    </>
  )
}

export default AutoRenewForm
